import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { residentialServices, commercialServices, testimonials } from '../mockData';
import { Home as HomeIcon, Sparkles, Truck, Building2, Layers, Square, HardHat, Star, CheckCircle2, Clock, Shield, Phone, ArrowRight, Zap, Key, Store, Stethoscope, Baby } from 'lucide-react';
import axios from 'axios';

const iconMap = {
  Home: HomeIcon,
  Sparkles,
  Truck,
  Building2,
  Layers,
  Square,
  HardHat,
  Key,
  Store,
  Stethoscope,
  Baby
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 0, comment: '', service: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reviews`);
        setReviews(res.data);
      } catch (e) {
        console.log('Could not fetch reviews');
      }
    };
    fetchReviews();
  }, []);

  // Combine real reviews with static testimonials as fallback
  const displayReviews = reviews.length > 0 
    ? reviews.map((r, i) => ({ id: r.review_id || i, name: r.name, rating: r.rating, comment: r.comment, service: r.service }))
    : testimonials;

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.rating || !reviewForm.comment) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, reviewForm);
      setReviewSubmitted(true);
      const res = await axios.get(`${BACKEND_URL}/api/reviews`);
      setReviews(res.data);
      setTimeout(() => {
        setShowReviewForm(false);
        setReviewSubmitted(false);
        setReviewForm({ name: '', rating: 0, comment: '', service: '' });
      }, 3000);
    } catch (e) {
      alert('Something went wrong. Please try again.');
    }
    setSubmittingReview(false);
  };

  return (
    <div className="min-h-screen">
      {/* First Time Customer Promo Banner */}
      <section className="bg-gradient-to-r from-green-500 to-green-600 py-1.5" data-testid="promo-banner">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-3 text-white text-center">
            <span className="text-sm font-black">10% OFF</span>
            <span className="text-xs font-medium">First Time Customers — Select "First Time Customer" at checkout</span>
            <Link to="/booking">
              <Button className="bg-white text-green-700 hover:bg-green-50 font-bold px-4 py-1 text-xs rounded-full h-auto">
                Book Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden min-h-[700px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1686178827149-6d55c72d81df" 
            alt="Professional cleaning service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/90 to-blue-900/80"></div>
          <div className="absolute top-20 right-20 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-4xl">
            <div className="hero-title mb-4">
              <div className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm px-6 py-2 rounded-full border border-yellow-400/30 mb-6">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-semibold">Trusted by 10,000+ Happy Customers</span>
              </div>
            </div>
            <h1 className="hero-title text-6xl md:text-8xl font-black mb-6 text-white leading-tight">
              <span className="gradient-text block">A Team</span> 
              <span className="block">Cleaning Service</span>
            </h1>
            <p className="hero-subtitle text-2xl md:text-3xl text-blue-100 mb-8 leading-relaxed font-light">
              Transform your space with <span className="text-yellow-400 font-semibold">professional cleaning</span>. 
              <br className="hidden md:block" />
              Sparkling results, guaranteed satisfaction.
            </p>
            <div className="hero-buttons flex flex-col sm:flex-row gap-4">
              <Link to="/booking">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-yellow-500/50 group">
                  Book Now
                  <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-3 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-7 text-xl font-bold backdrop-blur-sm bg-white/10">
                  View Services
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-8 mt-12 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" />
                <span className="font-semibold">100% Satisfaction</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-yellow-400" />
                <span className="font-semibold">Insured & Bonded</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-400" />
                <span className="font-semibold">Same-Day Service</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section with Category Selection */}
      <section className="py-24 bg-gradient-to-br from-blue-50 via-white to-yellow-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-blue-600 font-bold text-sm uppercase tracking-wider bg-blue-100 px-4 py-2 rounded-full">Our Services</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 text-gray-900">Professional Cleaning Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select your service type to explore our cleaning options
            </p>
          </div>

          {/* Category Selection */}
          {!selectedCategory ? (
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Residential */}
                <div
                  onClick={() => setSelectedCategory('residential')}
                  className="cursor-pointer group"
                >
                  <Card className="h-full border-2 border-gray-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    <div className="h-48 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1758273238370-3bc08e399620?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800" alt="Residential Cleaning" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-blue-900/40"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center">
                          <HomeIcon className="w-10 h-10 text-blue-600" />
                        </div>
                      </div>
                    </div>
                    <CardHeader className="text-center">
                      <CardTitle className="text-3xl text-gray-900">Residential Cleaning</CardTitle>
                      <CardDescription className="text-lg">
                        Professional cleaning for homes & apartments
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <ul className="text-gray-600 space-y-2 mb-6">
                        <li>• Regular House Cleaning</li>
                        <li>• Deep Cleaning</li>
                        <li>• Move In/Out Cleaning</li>
                        <li>• Airbnb/Rental Turnover</li>
                      </ul>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg">
                        View Residential Services
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Commercial */}
                <div
                  onClick={() => setSelectedCategory('commercial')}
                  className="cursor-pointer group"
                >
                  <Card className="h-full border-2 border-gray-200 hover:border-yellow-500 hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    <div className="h-48 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1770992176317-382f51aff35d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800" alt="Commercial Cleaning" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-yellow-900/40"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center">
                          <Building2 className="w-10 h-10 text-yellow-600" />
                        </div>
                      </div>
                    </div>
                    <CardHeader className="text-center">
                      <CardTitle className="text-3xl text-gray-900">Commercial Cleaning</CardTitle>
                      <CardDescription className="text-lg">
                        Professional cleaning for businesses & offices
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <ul className="text-gray-600 space-y-2 mb-6">
                        <li>• Office Cleaning</li>
                        <li>• Retail Store Cleaning</li>
                        <li>• Daycare Cleaning</li>
                        <li>• Medical/Dental Office</li>
                      </ul>
                      <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-6 text-lg">
                        View Commercial Services
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          ) : (
            /* Services Display after Category Selection */
            <div>
              {/* Back Button and Category Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${selectedCategory === 'commercial' ? 'bg-yellow-500' : 'bg-blue-600'} rounded-xl flex items-center justify-center`}>
                    {selectedCategory === 'commercial' ? (
                      <Building2 className="w-7 h-7 text-white" />
                    ) : (
                      <HomeIcon className="w-7 h-7 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {selectedCategory === 'commercial' ? 'Commercial Cleaning' : 'Residential Cleaning'}
                    </h3>
                    <p className="text-gray-600">
                      {selectedCategory === 'commercial' 
                        ? 'Professional cleaning for businesses' 
                        : 'Professional cleaning for homes'}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline"
                  onClick={() => setSelectedCategory(null)}
                  className="text-gray-600"
                >
                  ← Back to Categories
                </Button>
              </div>

              {/* Services Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {(selectedCategory === 'commercial' ? commercialServices : residentialServices).map((service, index) => {
                  const IconComponent = iconMap[service.icon] || Building2;
                  const isCommercial = selectedCategory === 'commercial';
                  return (
                    <Card key={service.id} className={`border-2 hover:shadow-xl transition-all ${isCommercial ? 'hover:border-yellow-400' : 'hover:border-blue-400'} group overflow-hidden`}>
                      <div className={`h-2 ${isCommercial ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' : 'bg-gradient-to-r from-blue-600 to-blue-400'} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                      <CardHeader>
                        <div className={`w-14 h-14 ${isCommercial ? 'bg-gradient-to-br from-yellow-400 to-yellow-500' : 'bg-gradient-to-br from-blue-600 to-blue-400'} rounded-xl flex items-center justify-center mb-3 group-hover:rotate-6 transition-transform`}>
                          <IconComponent className="w-7 h-7 text-white" />
                        </div>
                        <CardTitle className="text-xl">{service.name}</CardTitle>
                        <CardDescription className="text-sm">{service.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-500 mb-1">Starting from</p>
                        <div className="flex items-baseline gap-2">
                          <p className={`text-3xl font-black ${isCommercial ? 'text-yellow-600' : 'text-blue-600'}`}>${service.pricing.oneTime}</p>
                          <span className="text-xs text-gray-500">one-time</span>
                        </div>
                        {service.pricing.monthly && (
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <p className="text-lg font-bold text-green-600">${service.pricing.monthly}</p>
                            <span className="text-xs text-green-600">monthly</span>
                          </div>
                        )}
                        <Link to="/booking" state={{ selectedService: service.id, selectedCategory: service.category }}>
                          <Button className={`w-full mt-4 ${isCommercial ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-900' : 'bg-blue-600 hover:bg-blue-700 text-white'} font-semibold`}>
                            Book Now
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Switch Category */}
              <div className="text-center mt-8">
                <p className="text-gray-600 mb-3">
                  Looking for {selectedCategory === 'commercial' ? 'residential' : 'commercial'} cleaning?
                </p>
                <Button 
                  variant="outline"
                  onClick={() => setSelectedCategory(selectedCategory === 'commercial' ? 'residential' : 'commercial')}
                  className={selectedCategory === 'commercial' ? 'border-blue-500 text-blue-600' : 'border-yellow-500 text-yellow-600'}
                >
                  View {selectedCategory === 'commercial' ? 'Residential' : 'Commercial'} Services
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-yellow-600 font-bold text-sm uppercase tracking-wider bg-yellow-100 px-4 py-2 rounded-full">Why Choose Us</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 text-gray-900">The A Team Difference</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience cleaning services that exceed expectations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center border-2 hover:border-blue-400 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Quality Guaranteed</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">100% satisfaction guarantee on every cleaning service we provide</p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-yellow-400 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-yellow-600" />
                </div>
                <CardTitle className="text-xl">Fully Insured</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Bonded and insured for your complete peace of mind</p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-blue-400 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl">Flexible Scheduling</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Same-day service available with flexible booking options</p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-yellow-400 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-yellow-600" />
                </div>
                <CardTitle className="text-xl">Top Rated</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">5-star reviews from thousands of satisfied customers</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 bg-gradient-to-br from-blue-900 to-blue-950" id="reviews" data-testid="reviews-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-yellow-400 font-bold text-sm uppercase tracking-wider bg-yellow-400/20 px-4 py-2 rounded-full">Reviews</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 text-white">What Our Customers Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {displayReviews.slice(0, 6).map((review) => (
              <Card key={review.id} className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardHeader>
                  <div className="flex mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-white text-lg italic">&ldquo;{review.comment}&rdquo;</p>
                </CardHeader>
                <CardContent>
                  <p className="text-yellow-400 font-semibold">{review.name}</p>
                  <p className="text-blue-200 text-sm">{review.service}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Write a Review Button & Form */}
          <div className="text-center mt-12">
            {!showReviewForm ? (
              <Button
                onClick={() => setShowReviewForm(true)}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-8 py-4 text-lg rounded-full"
                data-testid="write-review-btn"
              >
                Write a Review
              </Button>
            ) : (
              <div className="max-w-lg mx-auto bg-white rounded-xl p-8 shadow-2xl text-left" data-testid="review-form">
                {reviewSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900">Thank You!</h3>
                    <p className="text-gray-600 mt-2">Your review has been submitted.</p>
                  </div>
                ) : (
                  <form onSubmit={handleReviewSubmit}>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Share Your Experience</h3>
                    
                    <div className="mb-4">
                      <label className="block text-gray-700 font-semibold mb-1">Your Name *</label>
                      <input
                        type="text"
                        required
                        value={reviewForm.name}
                        onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-blue-500 focus:outline-none"
                        placeholder="John Doe"
                        data-testid="review-name-input"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 font-semibold mb-1">Service Used</label>
                      <input
                        type="text"
                        value={reviewForm.service}
                        onChange={(e) => setReviewForm({...reviewForm, service: e.target.value})}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-blue-500 focus:outline-none"
                        placeholder="e.g. Deep Cleaning, Office Cleaning"
                        data-testid="review-service-input"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 font-semibold mb-2">Rating *</label>
                      <div className="flex gap-2 justify-center" data-testid="review-stars">
                        {[1,2,3,4,5].map((star) => (
                          <Star
                            key={star}
                            className={`w-10 h-10 cursor-pointer transition-colors ${
                              star <= reviewForm.rating 
                                ? 'text-yellow-400 fill-yellow-400' 
                                : 'text-gray-300'
                            }`}
                            onClick={() => setReviewForm({...reviewForm, rating: star})}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-gray-700 font-semibold mb-1">Your Review *</label>
                      <textarea
                        required
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-blue-500 focus:outline-none min-h-[100px]"
                        placeholder="Tell us about your experience..."
                        data-testid="review-comment-input"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button
                        type="submit"
                        disabled={submittingReview || !reviewForm.rating}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3"
                        data-testid="review-submit-btn"
                      >
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => { setShowReviewForm(false); setReviewForm({ name: '', rating: 0, comment: '', service: '' }); }}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6"
                        data-testid="review-cancel-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl md:text-6xl font-black mb-6 text-gray-900">Ready for a Cleaner Space?</h2>
          <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
            Book your professional cleaning service today and experience the A Team difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white px-10 py-7 text-xl font-bold">
                Book Now
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-10 py-7 text-xl font-bold">
                <Phone className="mr-2 w-6 h-6" />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
